import { useEffect, useState } from 'react'
import BrandShell from '../components/BrandShell.jsx'
import { supabase } from '../lib/supabase.js'
import { relativeTime } from '../lib/dateUtils.js'
import { useParticipant } from '../hooks/useParticipant.js'

export default function Admin() {
  const [tab, setTab] = useState('inbounds')
  return (
    <BrandShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <header>
          <h1 className="text-2xl font-bold text-brand-text">Admin</h1>
          <p className="text-sm text-brand-text-muted">Lennon only.</p>
        </header>

        <div className="flex gap-1 border-b border-brand-border overflow-x-auto">
          {[
            ['inbounds', 'Inbounds awaiting approval'],
            ['nominations', 'Best post nominations'],
            ['connections', 'LinkedIn connections'],
            ['notifications', 'Teams notifications'],
            ['controls', 'Controls'],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === id
                  ? 'border-brand-blue text-brand-blue'
                  : 'border-transparent text-brand-text-muted hover:text-brand-text'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'inbounds' && <InboundQueue />}
        {tab === 'nominations' && <Nominations />}
        {tab === 'connections' && <Connections />}
        {tab === 'notifications' && <Notifications />}
        {tab === 'controls' && <Controls />}
      </div>
    </BrandShell>
  )
}

function InboundQueue() {
  const { session } = useParticipant()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!supabase) return
    supabase
      .from('activities')
      .select('*, participants(name)')
      .eq('type', 'inbound')
      .is('inbound_approved', null)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setRows(data || []); setLoading(false) })
  }, [])

  async function decide(id, approved) {
    if (!supabase) return
    await supabase.from('activities').update({ inbound_approved: approved }).eq('id', id)
    setRows(prev => prev.filter(r => r.id !== id))
    fetch('/.netlify/functions/recompute-points', {
      method: 'POST',
      headers: session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {},
    }).catch(() => {})
    // Fire the celebratory Teams card only on approve, never on decline.
    if (approved && session?.access_token) {
      fetch('/.netlify/functions/notify-inbound-approved', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ activityId: id }),
      }).catch(err => console.warn('notify-inbound-approved failed:', err))
    }
  }

  if (loading) return <p className="text-sm text-brand-text-muted">Loading…</p>
  if (!rows.length) return <p className="text-sm text-brand-text-muted">Inbox zero. Nice.</p>

  return (
    <ul className="space-y-3">
      {rows.map(r => (
        <li key={r.id} className="bg-white border border-brand-border rounded-xl p-4 flex items-start gap-4">
          <div className="flex-1">
            <div className="text-sm font-semibold text-brand-text">{r.participants?.name}</div>
            <div className="text-xs text-brand-text-muted">{relativeTime(r.created_at)}</div>
            {r.notes && <div className="text-sm mt-2 text-brand-text">{r.notes}</div>}
            {r.screenshot_path && (
              <a
                href={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/inbound-screenshots/${r.screenshot_path}`}
                target="_blank" rel="noreferrer"
                className="text-xs text-brand-blue hover:underline"
              >
                View screenshot ↗
              </a>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => decide(r.id, true)} className="bg-brand-success text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:opacity-90">Approve</button>
            <button onClick={() => decide(r.id, false)} className="bg-brand-bg-muted text-brand-text text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-brand-border">Decline</button>
          </div>
        </li>
      ))}
    </ul>
  )
}

function Nominations() {
  const [rows, setRows] = useState([])

  useEffect(() => {
    if (!supabase) return
    supabase
      .from('activities')
      .select('*, participants(name), post_stats(*)')
      .eq('type', 'post')
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => setRows(data || []))
  }, [])

  async function nominate(id, value) {
    await supabase.from('activities').update({ best_post_nominee: value }).eq('id', id)
    setRows(prev => prev.map(r => r.id === id ? { ...r, best_post_nominee: value } : r))
  }

  return (
    <ul className="space-y-3">
      {rows.map(r => (
        <li key={r.id} className="bg-white border border-brand-border rounded-xl p-4 flex items-start gap-4">
          <div className="flex-1">
            <div className="text-sm font-semibold text-brand-text">{r.participants?.name}</div>
            <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-brand-blue truncate block max-w-xl hover:underline">{r.url}</a>
            <div className="text-xs text-brand-text-muted mt-1">
              {((r.post_stats || []).reduce((m, s) => Math.max(m, s?.impressions || 0), 0)).toLocaleString()} impressions · {(r.post_stats || []).reduce((m, s) => Math.max(m, s?.reactions || 0), 0)} reactions
            </div>
          </div>
          <button
            onClick={() => nominate(r.id, !r.best_post_nominee)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
              r.best_post_nominee
                ? 'bg-brand-gold text-white'
                : 'bg-brand-bg-muted text-brand-text hover:bg-brand-border'
            }`}
          >
            {r.best_post_nominee ? '★ Nominated' : 'Nominate'}
          </button>
        </li>
      ))}
    </ul>
  )
}

function Connections() {
  const { session } = useParticipant()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState(null)
  const [editing, setEditing] = useState({})
  const [diagBusy, setDiagBusy] = useState(false)
  const [diag, setDiag] = useState(null)

  async function runDiagnostic() {
    setDiagBusy(true)
    setDiag(null)
    try {
      const res = await fetch('/.netlify/functions/publora-diagnostic', {
        method: 'POST',
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      })
      const json = await res.json()
      setDiag({ status: res.status, body: json })
    } catch (err) {
      setDiag({ status: 'fetch_error', body: { error: err.message } })
    } finally {
      setDiagBusy(false)
    }
  }

  async function load() {
    setLoading(true)
    const { data } = await supabase
      .from('participants')
      .select('id, name, email, publora_user_id, publora_platform_id, connected_at, active')
      .order('name')
    setRows(data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function saveId(id, value) {
    const clean = (value || '').trim() || null
    await supabase
      .from('participants')
      .update({
        publora_platform_id: clean,
        connected_at: clean ? new Date().toISOString() : null,
      })
      .eq('id', id)
    setEditing(prev => ({ ...prev, [id]: undefined }))
    await load()
  }

  async function runSync() {
    setSyncing(true)
    setSyncResult(null)
    try {
      const res = await fetch('/.netlify/functions/sync-publora-connections', {
        method: 'POST',
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : {},
      })
      const json = await res.json()
      setSyncResult(json)
      await load()
    } catch (err) {
      setSyncResult({ error: err.message })
    } finally {
      setSyncing(false)
    }
  }

  if (loading) return <p className="text-sm text-brand-text-muted">Loading…</p>

  return (
    <div className="space-y-6">
      <div className="bg-brand-blue-tint/60 border border-brand-blue/20 rounded-xl p-4">
        <h3 className="font-semibold text-brand-text mb-1">How connections work</h3>
        <p className="text-sm text-brand-text mb-2">
          Teammates connect themselves by signing in at <code className="bg-white px-1 rounded">linkedin.climatise.com</code> and clicking Connect on the activation page. Each person gets a one-off Publora-hosted OAuth link that we generate on demand — no Publora dashboard access required.
        </p>
        <p className="text-sm text-brand-text-muted">
          Use the button below only if the auto-detect missed someone (rare: e.g. they closed the tab before the polling fired), or paste a <code className="bg-white px-1 rounded">linkedin-XYZ</code> id manually if needed.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={runSync}
          disabled={syncing}
          className="bg-brand-blue text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-brand-blue-hover disabled:opacity-60"
        >
          {syncing ? 'Re-syncing…' : 'Re-sync connections from Publora'}
        </button>
        <button
          onClick={runDiagnostic}
          disabled={diagBusy}
          className="bg-white text-brand-text text-sm font-semibold px-4 py-2 rounded-lg border border-brand-border hover:bg-brand-bg-soft disabled:opacity-60"
        >
          {diagBusy ? 'Checking…' : 'Run Publora API diagnostic'}
        </button>
      </div>
      {syncResult && (
        <div className="mt-1 text-xs bg-white border border-brand-border rounded-xl p-3 font-mono whitespace-pre-wrap">
          {JSON.stringify(syncResult, null, 2)}
        </div>
      )}
      {diag && (
        <div className="mt-1 bg-white border border-brand-border rounded-xl p-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <h4 className="font-semibold text-brand-text text-sm">Publora API health</h4>
            {diag.body?.env?.keyPreview && (
              <code className="text-[10px] text-brand-text-muted">{diag.body.env.keyPreview}</code>
            )}
          </div>
          {Array.isArray(diag.body?.checks) ? (
            <ul className="space-y-1.5">
              {diag.body.checks.map((c, i) => (
                <li key={i} className="text-sm flex gap-2">
                  <span>{c.ok ? '✅' : '❌'}</span>
                  <span className="font-medium text-brand-text">{c.name}</span>
                  <span className="text-brand-text-muted">— {c.detail || c.error}</span>
                </li>
              ))}
            </ul>
          ) : (
            <pre className="text-xs font-mono whitespace-pre-wrap">{JSON.stringify(diag.body, null, 2)}</pre>
          )}
          {Array.isArray(diag.body?.tokens) && diag.body.tokens.length > 0 && (
            <div className="border-t border-brand-border pt-3">
              <div className="text-xs font-semibold text-brand-text-muted uppercase tracking-wide mb-2">Token health</div>
              <ul className="space-y-1">
                {diag.body.tokens.map((t, i) => (
                  <li key={i} className="text-xs flex gap-2 font-mono">
                    <span>
                      {t.tokenStatus === 'valid' ? '🟢' :
                       t.tokenStatus === 'expiring_soon' ? '🟡' :
                       t.tokenStatus === 'expired' ? '🔴' : '⚪'}
                    </span>
                    <span className="text-brand-text">{t.username}</span>
                    <span className="text-brand-text-muted">{t.platformId}</span>
                    <span className="text-brand-text-muted">{t.tokenStatus}{t.tokenExpiresIn ? ` (${t.tokenExpiresIn})` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <ul className="space-y-2">
        {rows.map(r => {
          const current = editing[r.id] !== undefined ? editing[r.id] : (r.publora_platform_id || '')
          const dirty = current !== (r.publora_platform_id || '')
          const hasUser = !!r.publora_user_id
          const hasLinkedIn = !!r.publora_platform_id
          // After running the diagnostic, attach token health to each row by
          // matching on platformId so admins can see who needs to reconnect.
          const tokenInfo = diag?.body?.tokens?.find(t => t.platformId === r.publora_platform_id)
          return (
            <li key={r.id} className="bg-white border border-brand-border rounded-xl p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-semibold text-brand-text">{r.name}</div>
                <div className="text-xs text-brand-text-muted">{r.email}</div>
                <div className="text-[10px] text-brand-text-muted mt-1 flex gap-2 flex-wrap">
                  <span className={hasUser ? 'text-brand-success' : ''}>{hasUser ? '● Publora user' : '○ No Publora user'}</span>
                  <span className={hasLinkedIn ? 'text-brand-success' : ''}>{hasLinkedIn ? '● LinkedIn linked' : '○ LinkedIn not linked'}</span>
                  {tokenInfo && (
                    <span className={
                      tokenInfo.tokenStatus === 'valid' ? 'text-brand-success' :
                      tokenInfo.tokenStatus === 'expiring_soon' ? 'text-amber-700 font-semibold' :
                      tokenInfo.tokenStatus === 'expired' ? 'text-brand-danger font-semibold' :
                      'text-brand-text-muted'
                    }>
                      {tokenInfo.tokenStatus === 'valid' ? '🟢 token valid' :
                       tokenInfo.tokenStatus === 'expiring_soon' ? `🟡 token expiring in ${tokenInfo.tokenExpiresIn} — ask them to reconnect` :
                       tokenInfo.tokenStatus === 'expired' ? '🔴 token expired — must reconnect' :
                       `⚪ ${tokenInfo.tokenStatus}`}
                    </span>
                  )}
                </div>
              </div>
              <input
                value={current}
                onChange={e => setEditing(prev => ({ ...prev, [r.id]: e.target.value }))}
                placeholder="linkedin-XYZ (manual override)"
                className="flex-1 min-w-[200px] text-sm border border-brand-border rounded-lg px-3 py-1.5 font-mono"
              />
              <button
                onClick={() => saveId(r.id, current)}
                disabled={!dirty}
                className="text-xs font-semibold bg-brand-blue text-white px-3 py-1.5 rounded-lg hover:bg-brand-blue-hover disabled:opacity-40"
              >
                Save
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function Controls() {
  const { session } = useParticipant()
  const [busy, setBusy] = useState(null)
  const [results, setResults] = useState({})

  async function call(fn) {
    setBusy(fn)
    setResults(prev => ({ ...prev, [fn]: null }))
    try {
      const res = await fetch(`/.netlify/functions/${fn}`, {
        method: 'POST',
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : {},
      })
      const text = await res.text()
      let parsed = text
      try { parsed = JSON.parse(text) } catch (_) {}
      setResults(prev => ({ ...prev, [fn]: { status: res.status, body: parsed } }))
    } catch (err) {
      setResults(prev => ({ ...prev, [fn]: { status: 'fetch_error', body: err.message } }))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-3">
      <Action label="Recompute all points" desc="Re-runs scoring against all activities. Idempotent and safe." onClick={() => call('recompute-points')} busy={busy === 'recompute-points'} result={results['recompute-points']} />
      <Action label="Refresh stats now" desc="Pulls fresh impressions and engagement from Publora for every active post." onClick={() => call('refresh-all')} busy={busy === 'refresh-all'} result={results['refresh-all']} />
      <Action label="Send weekly digest" desc="Posts Friday's leaderboard summary to Teams and emails the team." onClick={() => call('send-weekly-digest')} busy={busy === 'send-weekly-digest'} result={results['send-weekly-digest']} />
      <Action label="Send Monday kickoff" desc="Posts the week-N kickoff card. Self-gates to the comp window." onClick={() => call('send-monday-kickoff')} busy={busy === 'send-monday-kickoff'} result={results['send-monday-kickoff']} />
      <Action label="Send final-stretch card" desc="Posts the T-N countdown card. Self-gates to the last 14 days." onClick={() => call('send-final-stretch')} busy={busy === 'send-final-stretch'} result={results['send-final-stretch']} />
      <Action label="Send test Teams message" desc="Posts a small hello card to TEAMS_WEBHOOK_URL so you can confirm the chat integration works." onClick={() => call('test-teams')} busy={busy === 'test-teams'} result={results['test-teams']} />
    </div>
  )
}

function Notifications() {
  const { session } = useParticipant()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState(null)

  const [migrationNeeded, setMigrationNeeded] = useState(false)

  async function load() {
    setLoading(true)
    setMigrationNeeded(false)
    const { data, error } = await supabase
      .from('ms_teams_log')
      .select('id, card_type, dedupe_key, tier, payload, posted_at')
      .order('posted_at', { ascending: false })
      .limit(40)
    if (error && /column.*(dedupe_key|tier)/i.test(error.message)) {
      setMigrationNeeded(true)
    }
    setRows(data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function sendCustom(e) {
    e.preventDefault()
    if (!title.trim() || !session?.access_token) return
    setSending(true)
    setSendResult(null)
    try {
      const res = await fetch('/.netlify/functions/send-custom-teams', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ title: title.trim(), text: text.trim() }),
      })
      const json = await res.json().catch(() => ({}))
      setSendResult({ status: res.status, body: json })
      if (res.ok) {
        setTitle(''); setText('')
        await load()
      }
    } catch (err) {
      setSendResult({ status: 'fetch_error', body: { error: err.message } })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-6">
      {migrationNeeded && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-semibold text-amber-900 mb-1">Run migration 004</h3>
          <p className="text-sm text-amber-900">
            The notification log needs the <code className="bg-white px-1 rounded">dedupe_key</code> and <code className="bg-white px-1 rounded">tier</code> columns. From the project root run <code className="bg-white px-1 rounded">node scripts/_apply-migration-004.mjs</code> and paste the printed SQL into the Supabase SQL editor.
          </p>
        </div>
      )}

      <div className="bg-brand-blue-tint/60 border border-brand-blue/20 rounded-xl p-4">
        <h3 className="font-semibold text-brand-text mb-1">How notifications work</h3>
        <p className="text-sm text-brand-text mb-2">
          Cards post to the Teams group chat via <code className="bg-white px-1 rounded">TEAMS_WEBHOOK_URL</code>. Each card type has a cooldown or one-shot guard so the chat stays signal, not noise. Quiet hours: 20:00–08:00 BST plus weekends. Tier 1 daily cap: 3 cards.
        </p>
        <p className="text-sm text-brand-text-muted">
          Kill switch: set <code className="bg-white px-1 rounded">TEAMS_NOTIFICATIONS_ENABLED=false</code> in Netlify env vars to mute everything without a deploy.
        </p>
      </div>

      <form onSubmit={sendCustom} className="bg-white border border-brand-border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold text-brand-text">Send a custom message</h3>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Title (e.g. 'Reminder: get a post out today')"
          className="w-full text-sm border border-brand-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-blue-tint"
        />
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
          placeholder="Body (optional)"
          className="w-full text-sm border border-brand-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-blue-tint resize-none"
        />
        <button
          type="submit"
          disabled={sending || !title.trim()}
          className="bg-brand-blue text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-brand-blue-hover disabled:opacity-60"
        >
          {sending ? 'Sending…' : 'Post to Teams'}
        </button>
        {sendResult && (
          <div className={`text-xs font-mono rounded-lg px-3 py-2 ${sendResult.status === 200 ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>
            <div className="font-semibold mb-1">{sendResult.status}</div>
            {JSON.stringify(sendResult.body, null, 2)}
          </div>
        )}
      </form>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-brand-text">Recent activity (last 40)</h3>
          <button onClick={load} className="text-xs text-brand-blue hover:underline">Refresh</button>
        </div>
        {loading ? <p className="text-sm text-brand-text-muted">Loading…</p> :
          rows.length === 0 ? <p className="text-sm text-brand-text-muted">Nothing posted yet.</p> :
          <ul className="space-y-1">
            {rows.map(r => {
              const deferred = r.payload?.deferred === true
              const title = deferred
                ? r.payload?.card?.attachments?.[0]?.content?.body?.[0]?.text
                : r.payload?.attachments?.[0]?.content?.body?.[0]?.text
              return (
                <li key={r.id} className="bg-white border border-brand-border rounded-lg px-3 py-2 text-sm flex items-center gap-3">
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
                    deferred ? 'bg-brand-warning/20 text-amber-900' :
                    r.tier === 'tier1' ? 'bg-brand-success/20 text-green-900' :
                    'bg-brand-bg-muted text-brand-text-muted'
                  }`}>
                    {deferred ? 'deferred' : (r.tier || 'sent')}
                  </span>
                  <span className="font-mono text-xs text-brand-text-muted">{r.card_type}</span>
                  <span className="flex-1 truncate text-brand-text">{title || '(no title)'}</span>
                  <span className="text-xs text-brand-text-muted whitespace-nowrap">{relativeTime(r.posted_at)}</span>
                </li>
              )
            })}
          </ul>
        }
      </div>
    </div>
  )
}

function Action({ label, desc, onClick, busy, result }) {
  return (
    <div className="bg-white border border-brand-border rounded-xl p-4 space-y-2">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="font-semibold text-brand-text">{label}</div>
          <p className="text-xs text-brand-text-muted">{desc}</p>
        </div>
        <button onClick={onClick} disabled={busy} className="bg-brand-blue text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-brand-blue-hover disabled:opacity-60">
          {busy ? 'Running…' : 'Run'}
        </button>
      </div>
      {result && (
        <div className={`text-xs font-mono rounded-lg px-3 py-2 whitespace-pre-wrap break-all ${
          result.status === 200 ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'
        }`}>
          <div className="font-semibold mb-1">{result.status}</div>
          {typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2)}
        </div>
      )}
    </div>
  )
}
